"use client";

import { useCallback, useContext, useState } from "react";
import Info from "./info";
import Success from "./success";
import Payment, { Schema } from "./payment";
import ErrorPage from "@/components/error";
import type { RazorpayOptions } from "react-razorpay";
import {
  initiatePayment as initiateCoursePayment,
  verifyPayment as verifyCoursePayment,
} from "@/queries/course-payment";
import RazorpayLoader from "../../razorpay-loader";
import { toast } from "sonner";
import { useAnalytics, Analytics } from "@/hooks/use-analytics";
import {
  getCountryCurrencyFromCountryCode,
  getCurrentTimestamp,
  getPaymentMethodsToHideRazorpay,
  getQueryParamsObject,
} from "@/utils/helpers";
import { DataContext, DataProvider, dataContext } from "@/contexts/cohort-data";
import { verifyUserKyc } from "@/queries/verify-user-kyc";
import { useSearchParams } from "next/navigation";
import useDevice from "@/hooks/use-device";

export type View = "PHONE_LANDING" | "PAYMENT_DETAILS" | "PAYMENT_SUCCESS";
export type DiscountDetails = {
  discount: number;
  discountType: string;
  discountVisibility: string;
};

const MainWrapper = ({ initialData }: { initialData: DataContext }) => {
  const [data, setData] = useState(initialData);

  return (
    <DataProvider data={data} setData={setData}>
      <Main />
    </DataProvider>
  );
};

function Main() {
  const searchParams = useSearchParams();

  const { data: contextData } = useContext(dataContext);
  const { track } = useAnalytics({ ...(contextData.gtm_source ?? {}) });

  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [view, setView] = useState<View>(
    contextData?.meta_data?.is_default_payment_page === false
      ? "PHONE_LANDING"
      : "PAYMENT_DETAILS"
  );
  const [successMessage, setSuccessMessage] = useState<string>();
  const [discountDetails, setDiscountDetails] = useState<DiscountDetails>({
    discount: 0,
    discountType: "",
    discountVisibility: "",
  });
  const { browser, os, url } = useDevice();

  const eventData = {
    url: url,
    creatorID: contextData?.creatorID,
    offeringStatus: contextData?.c_status,
    browser: browser,
    os: os,
    timestamp: getCurrentTimestamp(),
    product_type: "course",
  };

  const handleUpdateView = (view: View) => {
    setView(view);
  };

  const trackPaymentFailure = (
    data: Schema,
    totalAmount: number,
    reason: string
  ) => {
    track("payment_failure", {
      ...eventData,
      currency: contextData?.currency,
      amount: totalAmount,
      plan_id: contextData?.id,
      coupon_code: data?.couponCode,
      discount_value: discountDetails?.discount.toString(),
      discount_type: discountDetails?.discountType,
      discount_visibility: discountDetails?.discountVisibility,
      name: data.name,
      email: data.email,
      phone: data.phone,
      failure_reason: reason,
    });
  };

  const initiatePayment = useCallback(
    async (
      totalAmount: number,
      data: Schema,
      paymentMethod?: string,
      callback?: (event: string) => void
    ) => {
      try {
        if (!window.Razorpay)
          throw new Error("Razorpay is not ready. Please try again later.");

        const paymentMethodsToHide = getPaymentMethodsToHideRazorpay(
          paymentMethod || ""
        );

        setIsPaymentLoading(true);

        const payload = {
          type: "mag",
          shared_by_ref_code: searchParams?.get("ref") || "",
          paymentUrlQueryData: getQueryParamsObject(searchParams),
          planArray: [
            {
              plan_id: contextData.id,
              offer_id: data.couponCode || undefined,
            },
          ],
          course_type: "cohort",
          name: data.name,
          email: data.email,
          amount: totalAmount,
          applied_extra_offers:
            (data.offers || []).length > 0
              ? (data.offers || [])
                  .filter((offer) => offer.isApplied)
                  .map((offer) => offer?.id) || []
              : undefined,
          mobile: data?.phone.replace("+", ""),
          plan_id:
            contextData.__country !== "IN" ? contextData.plan_id : undefined,
          gst_details: data.needGst
            ? {
                gst_provided_by_user_at_time_of_purchase: data.needGst,
                gstin: data.gstin,
                company_name: data.companyName,
              }
            : null,
          kycDetails: data?.kycDetails,
          method: paymentMethod,
        };

        if (data?.kycDetails) {
          try {
            await verifyUserKyc({
              email: data.email,
              name: data.name,
              mobile: data.phone,
              offeringId: contextData._id,
              offeringType: contextData.type,
              kycDetails: data.kycDetails,
              creatorId: contextData.creatorID,
              isPostPaymentCall: false,
            });
          } catch {}
        }

        const { data: paymentData, status } = await initiateCoursePayment(
          payload
        );
        if (status === 200 && paymentData) {
          callback?.("payment_initiated");
          // TODO: Call analytics event to track payment initiated
          track("payment_initiated", {
            ...eventData,
            currency: contextData?.currency,
            amount: totalAmount,
            plan_id: contextData?.id,
            coupon_code: data?.couponCode,
            discount_value: discountDetails?.discount.toString(),
            discount_type: discountDetails?.discountType,
            discount_visibility: discountDetails?.discountVisibility,
            name: data.name,
            email: data.email,
            phone: data.phone,
          });
          track("AddToCart", {
            content_ids: `/${contextData.slug}`,
            content_name: contextData.title,
            content_type: "webinar",
            value: totalAmount,
            offer_code: data.couponCode ?? "",
            currency:
              contextData.currency ||
              getCountryCurrencyFromCountryCode(contextData.__country),
            num_items: 1 + (payload.applied_extra_offers?.length || 0),
          });

          if (contextData?.growth_plan) {
            window?.amplitude?.getInstance()?.logEvent("PAYMENT_INITIATED", {
              content_ids: `/${contextData.slug}`,
              offering: "cohort",
              event: "PAYMENT_INITIATED",
              deviceId: searchParams?.get("device_id") || "",
            });
          }

          setSuccessMessage(paymentData.successMessage);

          // NOTE: Call Razorpay here
          const options: RazorpayOptions = {
            name: paymentData?.payment_reason || "Payment",
            amount: "100",
            currency: paymentData?.currency || "INR",
            key: paymentData.rzpKey || null,
            order_id: paymentData.order_id || null,
            prefill: {
              email: paymentData.e_m || "",
              contact: paymentData.p_n || "",
              name: paymentData.f_n || "",
            },
            config: {
              display: {
                // @ts-ignore
                hide: paymentMethodsToHide || [],
              },
            },
            theme: { color: paymentData?.theme_color || "#6a1b9a" },
            handler: async ({
              razorpay_order_id,
              razorpay_payment_id,
              razorpay_signature,
            }) => {
              try {
                setIsPaymentLoading(true);
                const { data: verifyPaymentData, status: verifyPaymentStatus } =
                  await verifyCoursePayment({
                    razorpay_order_id:
                      razorpay_order_id || paymentData.order_id,
                    razorpay_signature,
                    razorpay_payment_id,
                    receipt_id: paymentData.receipt_id,
                  });
                if (verifyPaymentStatus === 200 && verifyPaymentData) {
                  // TODO: Call analytics event to track payment success

                  if (contextData?.growth_plan) {
                    window?.amplitude?.getInstance()?.logEvent("PURCHASE", {
                      deviceId: searchParams?.get("device_id") || "",
                      content_ids: `/${contextData.slug}`,
                      content_name: contextData.title,
                      content_type: "cohort",
                      value: contextData.price,
                      currency:
                        contextData.currency ||
                        getCountryCurrencyFromCountryCode(
                          contextData.__country
                        ),
                      num_items:
                        1 + (payload.applied_extra_offers?.length || 0),
                    });
                  }
                  track("Purchase", {
                    content_ids: `/${contextData.slug}`,
                    content_name: contextData.title,
                    content_type: "webinar",
                    value: contextData.price,
                    currency:
                      contextData.currency ||
                      getCountryCurrencyFromCountryCode(contextData.__country),
                    num_items: 1 + (payload.applied_extra_offers?.length || 0),
                  });

                  if (paymentData?.redirectURL?.length > 0) {
                    const isInstagram = navigator?.userAgent
                      ?.toLowerCase()
                      .includes("instagram");

                    setTimeout(() => {
                      if (isInstagram) {
                        const form = document.createElement("form");
                        form.action = paymentData?.redirectURL;
                        form.method = "GET";
                        document.body.appendChild(form);
                        form.submit();
                      } else {
                        window.location.href = paymentData?.redirectURL;
                      }
                    }, paymentData?.redirectTimeInMS ?? 100);
                  }
                  if (
                    !paymentData?.redirectURL ||
                    paymentData?.redirectTimeInMS > 100
                  ) {
                    setIsPaymentLoading(false);
                    handleUpdateView("PAYMENT_SUCCESS");
                    // showConfetti();
                    track("payment_success", {
                      ...eventData,
                      currency: contextData?.currency,
                      amount: totalAmount,
                      plan_id: contextData?.id,
                      coupon_code: data?.couponCode,
                      discount_value: discountDetails?.discount.toString(),
                      discount_type: discountDetails?.discountType,
                      discount_visibility: discountDetails?.discountVisibility,
                      name: data.name,
                      email: data.email,
                      phone: data.phone,
                    });
                  }
                } else {
                  setIsPaymentLoading(false);
                  toast.error("Payment failed", {
                    description: "Failed to verify payment.",
                  });
                  trackPaymentFailure(
                    data,
                    totalAmount,
                    "Failed to verify payment."
                  );
                }
              } catch {
                setIsPaymentLoading(false);
                toast.error("Payment failed", {
                  description: "Failed to verify payment.",
                });
                trackPaymentFailure(
                  data,
                  totalAmount,
                  "Failed to verify payment."
                );
              }
            },
          };
          const rzp = window.Razorpay(options);
          rzp.open();

          rzp.on("payment.failure", function (response: any) {
            toast.error("Payment failed", {
              description: response.error.description,
            });
            trackPaymentFailure(data, totalAmount, response.error.description);
          });
        }
      } catch (error) {
        callback?.("payment_failed");

        const errorMessage =
          (error as unknown as any)?.response?.data?.message ||
          (error as unknown as any)?.message ||
          "Payment failed due to some unknown error";
        toast.error("Payment failed", {
          description: errorMessage,
        });
        trackPaymentFailure(data, totalAmount, errorMessage);
      } finally {
        setIsPaymentLoading(false);
      }
    },
    [contextData, track]
  );

  if (!contextData)
    return (
      <div className="flex flex-col justify-center items-center min-h-screen p-12 bg-neutral-100">
        <ErrorPage
          title="Something went wrong"
          description="Course not found"
        />
      </div>
    );

  if (!contextData.is_course_ready_to_sell)
    return (
      <div className="flex flex-col justify-center items-center min-h-screen p-12 bg-neutral-100">
        <ErrorPage
          title=""
          image=""
          description="This course is not available."
        />
      </div>
    );

  // NOTE: Confirm c_status values from backend.
  if (
    !contextData.c_published_flag ||
    contextData.c_status?.toLocaleLowerCase() !== "publishedandselling"
  )
    return (
      <div className="flex flex-col justify-center items-center min-h-screen p-12 bg-neutral-100">
        <ErrorPage
          title=""
          image=""
          description="This service is no longer available"
        />
      </div>
    );

  if (view === "PAYMENT_SUCCESS") return <Success message={successMessage} />;

  return (
    <div className="flex flex-col justify-center items-center min-h-screen md:p-12 bg-neutral-100">
      <div className="flex-1 md:flex-initial w-full lg:w-[920px] bg-white  px-4 pt-8 pb-24 md:p-8 gap-8 md:rounded-2xl grid grid-cols-1 md:grid-cols-2 overflow-hidden">
        <Payment
          slug={contextData.slug}
          initiatePayment={initiatePayment}
          isPaymentLoading={isPaymentLoading}
          view={view}
          setDiscountDetails={setDiscountDetails}
          setIsPaymentLoading={setIsPaymentLoading}
        />
        <Info view={view} onUpdateView={handleUpdateView} {...contextData} />
      </div>
      <Analytics {...contextData.gtm_source} />
      <RazorpayLoader />
    </div>
  );
}

export default MainWrapper;
